/* eslint-disable indent */

import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import FilterBar from "../filter/FilterBar"
import {useRouter} from "next/router"
import {useTranslation} from "next-i18next"
import BikeSuggest from "../reusable/BikeSuggest"
import {CollectionName} from "../../constants"
import DataContext from "../../context/DataContext"
import {endpoints} from "../../constants/API"
import {defaultValue} from "../filter/Config"
import useOnScreen from "../../hooks/OnScreen"
import useCache from "../../hooks/useCache"
import LoadingOverlay from "../routeTransition/LoadingOverlay"
import Typography from "../reusable/Typography/Typography"
import useRemoveFbclid from "../../hooks/useRemoveFbclid"
import debounce from "lodash.debounce"
import BikesCollectionWrapper from "./BikesCollectionWrapper"
import RenderBikesGrid from "../bikeDetails/components/RenderBikesGrid"
import OrganizationContext from "../../context/OrganizationContext"
import {logger} from "../../utility/Helper"

export enum BannerOptions {
    asBanner = "As Banner",
    afterFilters = "After Filters"
}

type Props = {
    excludeOccasion?: boolean;
    productsCount?: number
};

const BikesCollectionContainer: React.FC<Props> = ({excludeOccasion = false, productsCount}) => {
	const {organizationSlug, organizationInitialized} = useContext(OrganizationContext)
	const {locale} = useRouter()
	const {t} = useTranslation()
	const {bikes, pageProps: {stickyBar}} = useContext(DataContext)
	const excludedOccasionBikes = excludeOccasion ? useMemo(() => bikes.filter(bike => !bike?.categories?.find(cat => cat.slug === "occasion")), [bikes]) : null
	const [initialPage, setInitialPage] = useState(0)
	const [filtersActive, setFiltersActive] = useState(false)
	const router = useRouter()
	const params = router.query
	const isBrandWorld = router?.pathname.includes("/brand-world")
	const getFiltersStatus = () => Boolean(Object.entries(params)?.filter(([key, value]) => key !== "sortOrder" && key !== "page" && key !== "searchValue").find(([key, value]) => value !== defaultValue))
	const searchActive = useMemo(() => Boolean(Object.entries(params)?.filter(([key, value]) => key === "searchValue")?.find(([key, value]) => value !== defaultValue)), [params.searchValue])
	const sortActive = useMemo(() => Boolean(Object.entries(params)?.filter(([key, value]) => key === "sortOrder")?.find(([key, value]) => value !== defaultValue)), [params.sortOrder])
	useEffect(() => {
		setFiltersActive(() => getFiltersStatus())
	}, [params, router.asPath])
	const [lazyLoadedPage, setLazyLoadedPage] = useState<number | null>(0)
	const [initialLoading, setInitialLoading] = useState<boolean | null>(false)
	const [filteredProducts, setFilteredProducts] = useState<any[]>([])
	const [unfilteredProducts, setUnfilteredProducts] = useState<any[]>(bikes)
	const [doneFetchingProducts, setDoneFetchingProducts] = useState<boolean>(false)
	const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState<boolean>(false)
	const filteredProductsCount = useRef<number | null>(null)
	const searchedProductsCount = useRef<number | null>(null)
	const bikesFoundTKey = filteredProductsCount.current === 1 ? "e-bike-found" : "products-found"
	const scrollPos = useRef<number | null>(null)
	const removeFbclid = useRemoveFbclid()

	useEffect(() => {
		resetBikes(setUnfilteredProducts, bikes)
		resetBikes(setFilteredProducts, [])
		setDoneFetchingProducts(false)
		const initialPageValue = Number(sessionStorage.getItem("page")) || 0
		const scroll = Number(sessionStorage.getItem("scrollPosition")) || 0
		if (initialPageValue === 0 || scroll < 5300) {
			setInitialPage(() => 0)
			setLazyLoadedPage(() => 0)
		}
	}, [params, router.asPath])
	const scrollIntoPosition = position => {
		window.scrollTo({top: position, behavior: "smooth"})
	}

	useEffect(() => {
		if (typeof window !== "undefined" && window) {
			const initialPageValue = Number(sessionStorage.getItem("page")) || 0
			const scroll = Number(sessionStorage.getItem("scrollPosition")) || 0
			setInitialPage(() => initialPageValue)
			setLazyLoadedPage(() => initialPageValue)

			scrollPos.current = scroll
		}
	}, [])
	const handleScroll = useCallback(debounce(() => {
		const newScrollY = window.scrollY
		scrollPos.current = Math.ceil(newScrollY)
		sessionStorage.setItem("scrollPosition", scrollPos.current.toString())
	}, 300), [scrollPos])

	useEffect(() => {
		window.addEventListener("scroll", handleScroll)
		return () => {
			window.removeEventListener("scroll", handleScroll)
		}
	}, [handleScroll])

	useEffect(() => {
		removeFbclid()
	}, [removeFbclid])

	const lazyLoadedUrl = useMemo(() => {
		const batchSize = Number(process.env.NEXT_PUBLIC_BATCH_SIZE)
		const baseParams = `locale=${locale}&limit=${batchSize}&customSale=${organizationSlug}&offset=${lazyLoadedPage * batchSize}`
		if (Boolean(process.env.NEXT_PUBLIC_CUSTOM_INSTANCE) && !filtersActive && !sortActive && !searchActive && organizationInitialized && organizationSlug && initialPage === 0 && initialPage === lazyLoadedPage && initialPage === 0 && lazyLoadedPage === 0) {
			return `${endpoints.filterProducts}?params=${JSON.stringify({...params})}&locale=${locale}&limit=${batchSize * (initialPage + 1)}&customSale=${organizationSlug}&offset=${0}&organization=${organizationSlug}`
		}

		if (initialPage > 0 && initialPage === lazyLoadedPage && scrollPos.current > 4000) {
			setInitialLoading(true)
			return `${endpoints.filterProducts}?params=${JSON.stringify({...params})}&locale=${locale}&limit=${batchSize * (initialPage + 1)}&customSale=${organizationSlug}&offset=${0}&organization=${organizationSlug}`
		}

		if (filtersActive) {
			return `${endpoints.filterProducts}?params=${JSON.stringify({...params})}&${baseParams}&organization=${organizationSlug}`
		}

		if (sortActive && lazyLoadedPage === 0 && initialPage === lazyLoadedPage && !searchActive) {
			return `${endpoints.filterProducts}?params=${JSON.stringify({...params})}&${baseParams}&organization=${organizationSlug}`
		}

		if (lazyLoadedPage > 0 && initialPage < lazyLoadedPage && filteredProducts?.length === 0 && !searchActive) {
			return `${endpoints.filterProducts}?params=${JSON.stringify({...params})}&${baseParams}`
		}

		if (searchActive) {
			return `${endpoints.search}?term=${JSON.stringify(params.searchValue)}&${baseParams}`
		}

		return null
	}, [locale, filteredProducts, unfilteredProducts, lazyLoadedPage, filtersActive, params, router.asPath])

	const {data, isLoading} = useCache(lazyLoadedUrl)

	useEffect(() => {
		if (!isLoadingMoreProducts && scrollPos.current > 3200 && document.documentElement.scrollHeight > 5363 && data && initialPage > 0 && initialPage === lazyLoadedPage) {
			scrollIntoPosition(scrollPos.current)
			setInitialLoading(false)
		}
	}, [data, filteredProducts, unfilteredProducts])

	useEffect(() => {
		if (isLoading) {
			setIsLoadingMoreProducts(true)
		}
	}, [isLoading])

	useEffect(() => {
		if (data) {
			if (initialPage > 0 && initialPage === lazyLoadedPage && filtersActive && data?.products) {
				filteredProductsCount.current = Number(data?.totalNumberOfProducts) || data?.products?.length
				setDoneFetchingProducts(filteredProductsCount.current === data?.products?.length || initialPage >= Math.round(productsCount / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setFilteredProducts(() => [...data.products])
			} else if (Boolean(process.env.NEXT_PUBLIC_CUSTOM_INSTANCE || false) && !filtersActive && !sortActive && !searchActive && organizationInitialized && organizationSlug && initialPage === 0 && initialPage === lazyLoadedPage && lazyLoadedPage === 0) {
				setDoneFetchingProducts(Number(productsCount) === data.products?.length || initialPage >= Math.round(productsCount / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setUnfilteredProducts(() => [...data.products])
			} else if (initialPage > 0 && initialPage === lazyLoadedPage && !filtersActive && !searchActive && data?.products) {
				setDoneFetchingProducts(Number(productsCount) === data.products?.length || initialPage >= Math.round(productsCount / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setUnfilteredProducts(() => [...data.products])
			} else if (filtersActive && data?.products) {
				filteredProductsCount.current = Number(data?.totalNumberOfProducts) || filteredProducts.concat(data.products)?.length
				setDoneFetchingProducts(filteredProductsCount.current === filteredProducts.concat(data.products)?.length || lazyLoadedPage >= Math.round(filteredProductsCount.current / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setFilteredProducts(prev => [...prev, ...data.products])
			} else if (sortActive && lazyLoadedPage === 0 && initialPage === lazyLoadedPage && !searchActive && data?.products) {
				setDoneFetchingProducts(Number(productsCount) === data.products?.length || initialPage >= Math.round(productsCount / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setUnfilteredProducts(() => [...data.products])
			} else if (lazyLoadedPage > 0 && initialPage < lazyLoadedPage && !searchActive && data?.products) {
				setDoneFetchingProducts(Number(productsCount) === unfilteredProducts.concat(data.products)?.length || lazyLoadedPage >= Math.round(productsCount / Number(process.env.NEXT_PUBLIC_BATCH_SIZE)))
				setUnfilteredProducts(prev => [...prev, ...data.products])
			} else if (searchActive && data?.direct) {
				searchedProductsCount.current = Number(data?.direct.totalNumberOfProducts) ? Number(data?.direct.totalNumberOfProducts) : lazyLoadedPage > 0 ? unfilteredProducts.concat(data?.direct?.bikes)?.length : data?.direct?.bikes?.length
				setDoneFetchingProducts(Number(searchedProductsCount.current) === (lazyLoadedPage > 0 ? unfilteredProducts.concat(data.direct.bikes)?.length : data.direct.bikes.length))
				setUnfilteredProducts(prev => lazyLoadedPage > 0 && data?.direct?.bikes ? [...prev, ...data.direct.bikes] : data?.direct?.bikes ? [...data.direct.bikes] : [])
			}

			setIsLoadingMoreProducts(false)
		}
	}, [data, params])

	const triggerFetchMore = () => {
		if (!isLoadingMoreProducts && !doneFetchingProducts) {
			sessionStorage.setItem("page", (lazyLoadedPage + 1).toString())
			setIsLoadingMoreProducts(() => true)
			setLazyLoadedPage(prevState => prevState + 1)
		}
	}

	const resetBikes = (setter:(arg0:any[])=>void, toBeSet) => {
		setter(toBeSet)
	}

	const endOfProductsListIndicator = useRef()
	const isOnScreen = useOnScreen(endOfProductsListIndicator)
	useEffect(() => {
		if (isOnScreen) {
			triggerFetchMore()
		}
	}, [isOnScreen])

	return (
		<div>
			{((isLoading && lazyLoadedPage === 0) || initialLoading) && !isBrandWorld ? <LoadingOverlay isVisible={isLoading} /> : null}

			<div className={`container mt-0 ${stickyBar && stickyBar.length ? "mt-md-4" : "mt-md-5"}`}/>
			<FilterBar
				isLoading={isLoading || initialLoading || isLoadingMoreProducts}
				productsList={excludeOccasion ? excludedOccasionBikes : bikes}
				filteredProductsList={filteredProducts || []}
				collectionName={CollectionName.AllBikes}
				filteredProductsLength={filtersActive ? filteredProductsCount.current :
                searchActive ? searchedProductsCount.current : productsCount}
                >
				<>
					{isLoading ? null : <div
						className={`t-0 pt-2 mb-2 container d-md-none ${filtersActive ? filteredProductsCount.current :
                        productsCount !== 0 ? "" : "ms-1 me-2 d-flex align-items-center justify-content-between"}`}>
						<Typography variant="bodyXSm"
                            semanticTag="span"
                            style={{color: "#6C7074"}}>
							{`${filtersActive ? filteredProductsCount.current : productsCount} 
                            ${t(bikesFoundTKey, {ns: "filters"})}`}
						</Typography>
					</div>}
					{isBrandWorld ? <RenderBikesGrid showOverflow={false}
					useLazyLoading={true}
						triggerFetchMore={triggerFetchMore}
						bikesList={filtersActive ? filteredProducts : unfilteredProducts}
						isLoadingMoreProducts={isLoading || initialLoading || isLoadingMoreProducts}
						doneFetchingProducts={doneFetchingProducts} /> :
						<BikesCollectionWrapper
						filteredProducts={filtersActive ? filteredProducts || [] : unfilteredProducts}
						isLoadingMoreProducts={isLoadingMoreProducts}
						doneFetchingProducts={doneFetchingProducts}
                        unfilteredProducts={unfilteredProducts}
                        filtersActive={filtersActive}
                        filteredProductsCount={filteredProductsCount.current}
						productsCount={productsCount}
						searchActive={searchActive}
						searchedProductsCount={searchedProductsCount.current}
                        triggerFetchMore={triggerFetchMore}
					/> }

				</>
			</FilterBar>
			{isBrandWorld || doneFetchingProducts || (filtersActive && !filteredProducts?.length) ? null : <div
            className={`unfiltered-prod-length products-${unfilteredProducts?.length}`}
                data-cypress-trigger-next-page="scroll-into-view"
                ref={endOfProductsListIndicator}
                style={{visibility: "hidden"}} />}
			{isBrandWorld ? null : <BikeSuggest t={t}/>}
		</div>
	)
}

export default BikesCollectionContainer
